import * as React from 'react'
import { parseQueryString } from '../utils'
import { Spinner, Errors } from '../@components'
import { apiClient } from "../services/appClient"
import appSettings from '../appSettings'
import { IError } from '../services/errorService'

enum ResetPasswordPageStages {
    Init,
    Questions,
    Password,
    Final,
    Error
}

interface IResetPasswordPageState {
    stage: ResetPasswordPageStages,
    password: string;
    isPasswordValid: boolean;
    confirmPassword: string;
    isConfirmPasswordValid: boolean;
    isSecurityAnswersInvalid: boolean[];
    securityQuestions: string[];
    securityAnswers: string[];
    error?: IError;
    reset_token: string;
    account_id: string;
    resetComplete: boolean;
    request: boolean;
}

export class ResetPasswordPage extends React.Component<{}, IResetPasswordPageState> {
    constructor(props: {}) {
        super(props);

        var params = parseQueryString(location.search);
        this.state = {
            password: "",
            isPasswordValid: true,
            confirmPassword: "",
            isConfirmPasswordValid: true,
            isSecurityAnswersInvalid: [],
            securityQuestions: [],
            securityAnswers: [],
            reset_token: params.reset_token,
            account_id: params.account_id,
            stage: ResetPasswordPageStages.Init,
            resetComplete: false,
            request: true
        }
        this.onAnswerChanged = this.onAnswerChanged.bind(this);
        this.onValdiateAnswers = this.onValdiateAnswers.bind(this);
        this.onPasswordChanged = this.onPasswordChanged.bind(this);
        this.onConfirmPasswordChanged = this.onConfirmPasswordChanged.bind(this);
        this.onReset = this.onReset.bind(this);
    }

    public componentDidMount() {
        apiClient.getResetPasswordQuestions(this.state.reset_token)
            .then(data => {
                this.setState({
                    securityQuestions: [data.question1, data.question2, data.question3],
                    error: undefined,
                    stage: ResetPasswordPageStages.Questions,
                    request: false
                });
            })
            .catch(error => {
                this.setState({
                    stage: ResetPasswordPageStages.Error,
                    error: error,
                    request: false
                });
            });
    }

    public render() {
        return (
            <form className="form-horizontal">
                {this.renderInternal()}
            </form>
        );
    }

    private renderInternal() {
        switch (this.state.stage) {
            case ResetPasswordPageStages.Questions:
                return this.renderQuestionsForm();
            case ResetPasswordPageStages.Password:
                return this.renderPasswordForm();
            case ResetPasswordPageStages.Error:
                return this.renderError();
            default:
                return this.renderFinal();
        }
    }

    public renderError() {
        return (
            <React.Fragment>
                <div className="page-header">
                    <h1>Oops! Something went wrong</h1>
                </div>
                <Errors error={this.state.error} />
            </React.Fragment>);
    }

    public renderQuestionsForm() {
        const questions = this.state.securityQuestions.map((question, index) =>
            <React.Fragment key={"answer" + index}>
                <div className="form-group">
                    <label className="col-sm-3">Security Question {index + 1}:</label>
                    <label className="col-sm-9 question-text">{question}</label>
                </div>
                <div className="form-group">
                    <label className="col-sm-3" >Answer:<span className="text-required">*</span></label>
                    <div className={"col-sm-9" + (this.state.isSecurityAnswersInvalid[index] ? " has-error" : "")}>
                        <input type="text" className="form-control answer" value={this.state.securityAnswers[index] || ""} onChange={(e) => this.onAnswerChanged(e, index)} />
                    </div>
                </div>
            </React.Fragment>
        );

        return (
            <React.Fragment>
                <div className="page-header">
                    <h1>Please Answer These Security Questions</h1>
                </div>
                <p>
                    By answering these questions, you are helping us verify you are the account holder.<br />
                    <strong>Please be aware your account will be locked out after 5 unsuccessful attempts to provide valid answers to your security questions</strong>
                </p>
                <p>
                    Please provide your answer to each of the following questions:
                </p>
                <Errors error={this.state.error} />
                {questions}
                <div className="form-group">
                    <div className="col-sm-offset-3 col-sm-9">
                        <button type="submit" className="btn btn-wmata" onClick={this.onValdiateAnswers} disabled={this.state.request}>Submit</button>
                        <Spinner show={this.state.request} />
                    </div>
                </div>
            </React.Fragment>
        );
    }

    private onAnswerChanged(e: React.ChangeEvent<HTMLInputElement>, index: number) {
        const answers = this.state.securityAnswers.concat([]);
        const answersStatuses = this.state.isSecurityAnswersInvalid.concat([]);
        answers[index] = e.target.value;
        answersStatuses[index] = false;
        this.setState({
            securityAnswers: answers,
            isSecurityAnswersInvalid: answersStatuses
        });
    }

    private onValdiateAnswers(e: React.MouseEvent<HTMLButtonElement>) {
        e.preventDefault();

        const isAnswersInvalid: boolean[] = [];
        this.state.securityQuestions.forEach((val, index) => {
            isAnswersInvalid[index] = !this.state.securityAnswers[index];
        });
        if (isAnswersInvalid.some(x => x)) {
            this.setState({
                isSecurityAnswersInvalid: isAnswersInvalid
            });
            return;
        }

        this.setState({
            request: true
        });

        const data = {
            securityToken: this.state.reset_token,
            questions: this.state.securityQuestions,
            answers: this.state.securityAnswers,
        };

        apiClient.validateResetPasswordAnswers(data)
            .then(data => {
                this.setState({
                    error: undefined,
                    stage: ResetPasswordPageStages.Password,
                    request: false
                });
            })
            .catch(error => {
                this.setState({
                    error: error,
                    request: false
                });
            });
    }


    public renderPasswordForm() {
        const passwordClasses = "form-group" + (this.state.isPasswordValid ? "" : " has-error");
        const confirmPasswordClasses = "form-group" + (this.state.isConfirmPasswordValid ? "" : " has-error");

        return (
            <React.Fragment>
                <div className="page-header">
                    <h1>Set Your New Account Password</h1>
                </div>
                <p>Provide a new password for your account.</p>
                <p>If you are not able to reset your password, please call our SmarTrip&reg; Regional Customer Service Center at {appSettings.SmarTripRegionalCustomerServiceCenterPhone} for assistance.</p>
                <Errors error={this.state.error} />
                <div className={passwordClasses}>
                    <label htmlFor="password" className="col-sm-2 control-label">New password:<span className="text-required">*</span></label>
                    <div className="col-sm-5">
                        <input type="password" className="form-control" name="password" id="password" value={this.state.password} placeholder="New password" onChange={this.onPasswordChanged}/>
                    </div>
                </div>
                <div className={confirmPasswordClasses}>
                    <label htmlFor="password" className="col-sm-2 control-label">Re-enter password:<span className="text-required">*</span></label>
                    <div className="col-sm-5">
                        <input type="password" className="form-control" name="confirmPassword" id="confirmPassword" value={this.state.confirmPassword} placeholder="New password" onChange={this.onConfirmPasswordChanged}/>
                    </div>
                </div>
                <p>Your password is case-sensitive and must contain at least 8 characters, including letters, at least two numbers, and at least one of the following special characters !@#$%^&*~/"()_=+[]|,.?-.</p>
                <div className="form-group">
                    <div className="col-sm-offset-2 col-sm-10">
                        <button type="submit" className="btn btn-wmata" onClick={this.onReset} disabled={this.state.request}>Submit</button>
                        <Spinner show={this.state.request}/>
                    </div>
                </div>
            </React.Fragment>
        );
    }

    public renderFinal() {
        return (
            <div className="page-header">
                <h1>Password Was Successfully Reset</h1>
            </div>
        );
    }

    private onPasswordChanged(e: React.ChangeEvent<HTMLInputElement>) {
        const value = e.target.value;
        this.setState({
            password: value,
            isPasswordValid: true
        });
    }

    private onConfirmPasswordChanged(e: React.ChangeEvent<HTMLInputElement>) {
        const value = e.target.value;
        this.setState({
            confirmPassword: value,
            isConfirmPasswordValid: true
        });
    }

    private onReset(e: React.MouseEvent<HTMLButtonElement>) {
        e.preventDefault();

        const isPasswordValid = !!this.state.password;
        const isConfirmPasswordValid = !!this.state.confirmPassword && this.state.confirmPassword === this.state.password;

        if (!this.state.password) {
            this.setState({
                isPasswordValid,
                isConfirmPasswordValid
            });
            return;
        }

        this.setState({
            request: true
        });

        const data = {
            securityToken: this.state.reset_token,
            questions: this.state.securityQuestions,
            answers: this.state.securityAnswers,
            newPassword: this.state.password,
            confirmPassword: this.state.confirmPassword
        };

        apiClient.resetPassword(data)
            .then(data => {
                this.setState({
                    error: undefined,
                    stage: ResetPasswordPageStages.Final,
                    request: false
                });
            })
            .catch(error => {
                this.setState({
                    error: error,
                    request: false
                });
            });
    }
}


// WEBPACK FOOTER //
// ./src/pages/reset.tsx